import React, {useState, useEffect, forwardRef, useImperativeHandle} from 'react';
import {Modal, Select, Row, Col, Icon} from 'antd';
import {message} from "../../../../../../utils/message";

const {Option} = Select;

const initialConditions = [{
    field: {key: "", value: ""},
    option: [{key: "", value: ""}],
    operate: "is",
    method: "includes"
}];

const ShowRuleModal = forwardRef((props, ref) => {
    const {dataSourceHold, fieldName, onOk} = props;

    const [isVisible, setIsVisible] = useState(false);
    const [conditions, setConditions] = useState([initialConditions[0]]);
    const [firstOptions, setFirstOptions] = useState([]);
    const [secondOptions, setSecondOptions] = useState([]);

    useEffect(() => {
        if (conditions?.length && showRules(conditions)) {
            const updatedSecondOptions = conditions.map(condition => {
                return getOptions(condition.field.key)
            });
            setSecondOptions(updatedSecondOptions);
        }
    }, [firstOptions, conditions]);

    useEffect(() => {
        setFirstOptions(selectData());
    }, [dataSourceHold]);

    const SELECTED_FIELD_TYPES = [3, 4];

    const selectData = () => {
        const filteredItems = dataSourceHold.filter(item => SELECTED_FIELD_TYPES.includes(item.fieldType));
        return filteredItems.map(item => {
            const {dbFieldName, fieldName, fieldFillRules: [firstRule] = []} = item;
            const keyValueMaps = firstRule?.keyValueMaps ?? {};

            if (typeof keyValueMaps !== 'object' || Array.isArray(keyValueMaps)) {
                console.warn('fieldFillRules[0].keyValueMaps is not an object.', item);
                return null;
            }

            return {
                key: dbFieldName,
                value: fieldName,
                options: Object.entries(keyValueMaps).map(([key, value]) => ({key, value}))
            };
        }).filter(item => item !== null && item.key);
    };

    const showRules = (rules = []) => {
        return rules.every(condition => {
            const hasFieldValues = condition.field.key && condition.field.value;
            const hasOptionValues = condition?.option?.length && condition.option.every(opt => opt.key && opt.value);
            return hasFieldValues && hasOptionValues;
        })
    }

    const addCondition = () => {
        if (conditions.length < 2) {
            setConditions(prevConditions => [...prevConditions, {...initialConditions[0]}]);
        }
    };

    const removeCondition = (index) => {
        setConditions(conditions.filter((_, idx) => idx !== index));
    };

    const getOptions = (key) => {
        return firstOptions.find(item => item.key === key)?.options || [];
    };

    useImperativeHandle(ref, () => {
        return {
            setData
        }
    })

    const setData = (conditions) => {
        setConditions(conditions)
        setIsVisible(true)
    };

    const renderConditions = () => {
        return conditions && conditions.map((condition, index) => (
            <div key={index}
                 style={{marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-around'}}>
                {index === conditions.length - 1 && conditions.length !== 1 ?
                    <Select
                        style={{width: '100px'}}
                        placeholder="请选择关系"
                        value={condition.operate}
                        onChange={(e) => {
                            setConditions(prevConditions =>
                                prevConditions.map(cond =>  ({...cond, operate: e}))
                            );
                        }}
                    >
                        <Option value="or">或</Option>
                        <Option value="is">且</Option>
                    </Select> :
                    <Col span={2}>
                        <div style={{float: 'right'}}>当</div>
                    </Col>
                }
                <Col span={9}>
                    <Select
                        style={{width: '100%'}}
                        placeholder="请选择字段"
                        value={condition.field.key || undefined}
                        onChange={(e, option) => {
                            setConditions(prevConditions =>
                                prevConditions.map(cond =>
                                    cond === condition
                                        ? {
                                            ...cond,
                                            field: {
                                                ...cond.field,
                                                key: e,
                                                value: option.props.children
                                            },
                                            option: [{key: "", value: ""}]
                                        }
                                        : cond
                                )
                            );
                            setSecondOptions(getOptions(e));
                        }}
                    >
                        {firstOptions.map((item) => (
                            <Option key={item.key} value={item.key}>{item.value}</Option>
                        ))}
                    </Select>
                </Col>
                <Col span={2}>
                    <div style={{float: 'right'}}>选择</div>
                </Col>
                <Col span={9}>
                    <Select
                        style={{width: '100%'}}
                        mode="multiple"
                        placeholder="请选择选项"
                        value={condition.option.filter(opt => opt.key !== "").map(opt => opt.key) || undefined}
                        onChange={(selectedKeys) => {
                            const updatedOptions = selectedKeys.map(key => {
                                // 根据key查找对应的value
                                const foundItem = getOptions(condition.field.key).find(item => item.key === key);
                                return {
                                    key,
                                    value: foundItem?.value || ''
                                };
                            });

                            setConditions(prevConditions =>
                                prevConditions.map(cond =>
                                    cond === condition
                                        ? {
                                            ...cond,
                                            option: updatedOptions
                                        }
                                        : cond
                                )
                            );
                        }}
                    >
                        {getOptions(condition.field.key).map((item) => (
                            <Option key={item.key} value={item.key}>{item.value}</Option>
                        ))}
                    </Select>
                </Col>
                <Col span={2}>
                    {index === conditions.length - 1 && conditions.length < 2 ?
                        <Icon type="plus-circle" style={{fontSize: '20px', color: '#1890ff', cursor: 'pointer'}}
                              onClick={addCondition}/> :
                        <Icon type="minus-circle" style={{fontSize: '20px', color: '#1890ff', cursor: 'pointer'}}
                              onClick={() => removeCondition(index)}/>}
                </Col>
            </div>
        ));
    };

    const handleOk = () => {
        if (!showRules(conditions)) {
            message.info("请完善规则");
            return;
        }
        setIsVisible(false);
        onOk && onOk(conditions);
    };

    const handleCancel = () => {
        setIsVisible(false);
    };

    return (
        <>
            <Modal
                title="显示规则设置"
                visible={isVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                width={700}
                centered
            >
                <Row gutter={10}>
                    {renderConditions()}
                </Row>
                <Row gutter={10}>
                    <Col span={22}><span style={{color: "red"}}>显示字段“{fieldName}”</span></Col>
                    <Col span={2}>
                        {conditions && conditions.length > 2 &&
                            <Icon type="plus-circle" style={{fontSize: '20px', color: '#1890ff', cursor: 'pointer'}}
                                  onClick={addCondition}/>}
                    </Col>
                </Row>
            </Modal>
        </>
    );
});

export default ShowRuleModal;
